import type Konva from 'konva';
import type {} from 'konva/lib/Node';
import type { RefObject } from 'react';
import { create } from 'zustand';
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '../constants';
import type {
  CanvasHistoryState,
  CanvasImage,
  EditingStyle,
  LogoProps,
  LogoPropsUpdater,
  SelectionRectState,
  SelectionType,
  TextItem,
  TextUpdater,
} from '../types';

const CENTER_X = CANVAS_WIDTH / 2;
const CENTER_Y = CANVAS_HEIGHT / 2;

const DEFAULT_TEXT_ITEMS: TextItem[] = [
  {
    id: 'default-title',
    text: 'Welcome to Canvas Editor',
    x: CENTER_X - 200,
    y: CENTER_Y - 100,
    width: 400,
    color: '#2c3e50',
    fontFamily: 'Arial',
    fontSize: 32,
    lineHeight: 1.2,
    letterSpacing: 0,
    align: 'center',
    animation: 'none',
    zIndex: 1,
    animationDuration: 0,
    animationDelay: 0,
  },
  {
    id: 'default-subtitle',
    text: 'Click on this text to edit it or add your own\nPress Enter for new lines',
    x: CENTER_X - 150,
    y: CENTER_Y - 20,
    width: 300,
    color: '#7f8c8d',
    fontFamily: 'Arial',
    fontSize: 20,
    lineHeight: 1.5,
    letterSpacing: 0,
    align: 'center',
    animation: 'none',
    zIndex: 2,
    animationDuration: 0,
    animationDelay: 0,
  },
];

const DEFAULT_LOGO_PROPS: LogoProps = {
  x: CENTER_X - 100,
  y: CENTER_Y + 80,
  width: 200,
  height: 80,
  animation: 'none',
};

const getSelectedIds = (selection: SelectionType): string[] => {
  if (!selection) return [];
  if (selection === 'canvas') return [];
  return Array.isArray(selection) ? selection : [selection];
};

const validateImageUrl = (url: string | null): boolean => {
  if (!url) return true;
  return /^(data:|https?:\/\/)/i.test(url);
};

const generateTimeBasedId = (prefix: string): string => {
  const now = new Date();
  const timestamp = now.getTime();
  const seconds = now.getSeconds();
  const milliseconds = now.getMilliseconds();
  const uniqueSuffix = `${timestamp}-${seconds}${milliseconds}-${performance.now().toString().replace('.', '')}`;
  return `${prefix}-${uniqueSuffix}`;
};

const getHighestZIndex = (state: CanvasState): number => {
  const textZIndices = state.texts.map((t) => t.zIndex || 0);
  const imageZIndices = state.canvasImages.filter((img) => !img.deleted).map((img) => img.zIndex || 0);
  const logoZIndex = state.logoImage ? state.logoProps.zIndex || 0 : 0;

  const allZIndices = [...textZIndices, ...imageZIndices];
  if (logoZIndex > 0) allZIndices.push(logoZIndex);

  return allZIndices.length > 0 ? Math.max(...allZIndices) : 0;
};

interface CanvasState {
  texts: TextItem[];
  setTexts: (updater: TextItem[] | TextUpdater, saveHistory?: boolean) => void;
  logoProps: LogoProps;
  setLogoProps: (updater: LogoProps | LogoPropsUpdater, saveHistory?: boolean) => void;
  backgroundColor: string;
  setBackgroundColor: (color: string, saveHistory?: boolean) => void;
  bgImage: string | null;
  setBgImage: (img: string | null, saveHistory?: boolean) => void;
  logoImage: string | null;
  setLogoImage: (img: string | null, saveHistory?: boolean) => void;
  canvasImages: CanvasImage[];
  addImage: (url: string, options?: { width?: number; height?: number; x?: number; y?: number }) => string | null;
  updateImage: (id: string, props: Partial<CanvasImage>) => void;
  removeImage: (id: string) => void;
  selectedId: SelectionType;
  setSelectedId: (id: SelectionType) => void;
  isEditingTextId: string | null;
  setIsEditingTextId: (id: string | null) => void;
  editingStyle: EditingStyle;
  setEditingStyle: (style: EditingStyle) => void;
  previewMode: boolean;
  setPreviewMode: (mode: boolean) => void;
  resetCanvas: () => void;
  undoChange: () => void;
  redoChange: () => void;
  handleTextTransform: (id: string) => void;
  handleLogoTransform: () => void;
  handleTextDoubleClick: (id: string) => void;
  handleLogoDoubleClick: () => void;
  deleteSelected: () => void;
  handleMultiSelect: (id: string, addToSelection: boolean) => void;
  stageRef: RefObject<Konva.Stage>;
  logoRef: RefObject<Konva.Image>;
  trRef: RefObject<Konva.Transformer>;
  textRefs: RefObject<Map<string, Konva.Text>>;
  inputRef: RefObject<HTMLTextAreaElement>;
  fileInputRef: RefObject<HTMLInputElement>;
  logoInputRef: RefObject<HTMLInputElement>;
  getRef: <K extends keyof RefTypes>(name: K) => RefTypes[K];
  saveStateToHistory: () => void;
  history: CanvasHistoryState[];
  setHistory: (history: CanvasHistoryState[]) => void;
  future: CanvasHistoryState[];
  setFuture: (future: CanvasHistoryState[]) => void;
  globalAnimationDelay: number;
  setGlobalAnimationDelay: (delay: number) => void;
  updateElementOrder: () => void;
  normalizeZIndices: () => void;
  selectionRect: SelectionRectState;
  isMultiSelecting: boolean;
  selectedIds: string[];
  updateMultipleTexts: (properties: Partial<TextItem>) => void;
  handleMultiTransform: (scaleX: number, scaleY: number, rotation: number) => void;
  moveSelectedElements: (dx: number, dy: number) => void;
  multiSelection: string[];
}

interface RefTypes {
  stageRef: RefObject<Konva.Stage>;
  logoRef: RefObject<Konva.Image>;
  trRef: RefObject<Konva.Transformer>;
  textRefs: RefObject<Map<string, Konva.Text>>;
  inputRef: RefObject<HTMLTextAreaElement>;
  fileInputRef: RefObject<HTMLInputElement>;
  logoInputRef: RefObject<HTMLInputElement>;
}

const refs: RefTypes = {
  stageRef: { current: null as unknown as Konva.Stage },
  logoRef: { current: null as unknown as Konva.Image },
  trRef: { current: null as unknown as Konva.Transformer },
  textRefs: { current: new Map<string, Konva.Text>() },
  inputRef: { current: null as unknown as HTMLTextAreaElement },
  fileInputRef: { current: null as unknown as HTMLInputElement },
  logoInputRef: { current: null as unknown as HTMLInputElement },
};

const initialState: CanvasState = {
  texts: [...DEFAULT_TEXT_ITEMS],
  logoProps: { ...DEFAULT_LOGO_PROPS },
  backgroundColor: '#ffffff',
  bgImage: null,
  logoImage: null,
  canvasImages: [],
  selectedId: null,
  isEditingTextId: null,
  previewMode: false,
  selectionRect: { startX: 0, startY: 0, width: 0, height: 0, visible: false },
  isMultiSelecting: false,
  selectedIds: [],
  editingStyle: { top: 0, left: 0, width: 0, height: 0 },
  globalAnimationDelay: 0,
  history: [],
  future: [],
  setTexts: () => {},
  setLogoProps: () => {},
  setBackgroundColor: () => {},
  setBgImage: () => {},
  setLogoImage: () => {},
  addImage: () => '',
  updateImage: () => {},
  removeImage: () => {},
  setSelectedId: () => {},
  setIsEditingTextId: () => {},
  setEditingStyle: () => {},
  setPreviewMode: () => {},
  resetCanvas: () => {},
  undoChange: () => {},
  redoChange: () => {},
  handleTextTransform: () => {},
  handleLogoTransform: () => {},
  handleTextDoubleClick: () => {},
  handleLogoDoubleClick: () => {},
  deleteSelected: () => {},
  handleMultiSelect: () => {},
  stageRef: refs.stageRef,
  logoRef: refs.logoRef,
  trRef: refs.trRef,
  textRefs: refs.textRefs,
  inputRef: refs.inputRef,
  fileInputRef: refs.fileInputRef,
  logoInputRef: refs.logoInputRef,
  getRef: (name) => refs[name],
  saveStateToHistory: () => {},
  setHistory: () => {},
  setFuture: () => {},
  setGlobalAnimationDelay: () => {},
  updateElementOrder: () => {},
  normalizeZIndices: () => {},
  updateMultipleTexts: () => {},
  handleMultiTransform: () => {},
  moveSelectedElements: () => {},
  multiSelection: [],
};

export const useCanvasStore = create<CanvasState>((set, get) => ({
  ...initialState,

  setTexts: (updater, saveHistory = false) => {
    if (saveHistory) get().saveStateToHistory();
    if (typeof updater === 'function') {
      set({ texts: [...updater(get().texts)] });
    } else {
      set({ texts: [...updater] });
    }
  },

  setLogoProps: (updater, saveHistory = false) => {
    if (saveHistory) get().saveStateToHistory();
    if (typeof updater === 'function') {
      set({ logoProps: { ...updater(get().logoProps) } });
    } else {
      set({ logoProps: { ...updater } });
    }
  },

  setBackgroundColor: (color, saveHistory = false) => {
    if (saveHistory) get().saveStateToHistory();
    set({ backgroundColor: color });
  },

  setBgImage: (img, saveHistory = false) => {
    if (img && !validateImageUrl(img)) return;

    if (img === null && get().bgImage !== null) {
      get().saveStateToHistory();
    } else if (saveHistory && get().bgImage !== img) {
      get().saveStateToHistory();
    }

    set({ bgImage: img });
  },

  setLogoImage: (img, saveHistory = false) => {
    if (img && !validateImageUrl(img)) return;

    if (img === null && get().logoImage !== null) {
      get().saveStateToHistory();
    } else if (saveHistory && get().logoImage !== img) {
      get().saveStateToHistory();
    }

    set({ logoImage: img });

    if (img) {
      const id = generateTimeBasedId('image');
      const { logoProps } = get();
      const newImage: CanvasImage = {
        id,
        url: img,
        x: logoProps.x,
        y: logoProps.y,
        width: logoProps.width,
        height: logoProps.height,
        animation: logoProps.animation,
      };

      set((state) => ({
        canvasImages: [...state.canvasImages, newImage],
        selectedId: 'logo',
      }));
    }
  },

  canvasImages: [],
  addImage: (url, options) => {
    if (!validateImageUrl(url)) return null;

    const state = get();
    const highestZIndex = getHighestZIndex(state);
    const newZIndex = highestZIndex + 1;

    const id = generateTimeBasedId('image');

    const width = options?.width || 200;
    const height = options?.height || 200;
    const x = options?.x !== undefined ? options.x : (CANVAS_WIDTH - width) / 2;
    const y = options?.y !== undefined ? options.y : (CANVAS_HEIGHT - height) / 2;

    const newImage: CanvasImage = {
      id,
      url,
      x,
      y,
      width,
      height,
      animation: 'none',
      zIndex: newZIndex,
    };

    set((state) => ({
      canvasImages: [...state.canvasImages, newImage],
      selectedId: id,
    }));

    setTimeout(() => get().updateElementOrder(), 100);

    return id;
  },

  updateImage: (id, updates) => {
    if (updates.url && !validateImageUrl(updates.url)) return;

    set((state) => {
      const updatedImages = state.canvasImages
        .map((img) => {
          if (img.id === id) {
            const updatedImage = { ...img };
            if (updates.url === undefined) updates.url = updatedImage.url;
            if (updates.opacity === undefined && updates.animation !== undefined) {
              updates.opacity = updatedImage.opacity || 1;
            }
            return { ...updatedImage, ...updates };
          }
          return img;
        })
        .filter((img) => !img.deleted);

      return { canvasImages: updatedImages };
    });

    const state = get();
    if (state.stageRef.current) {
      const layer = state.stageRef.current.findOne('Layer') as Konva.Layer;
      if (layer) layer.batchDraw();
    }
  },

  removeImage: (id) => {
    get().saveStateToHistory();

    set((state) => ({
      canvasImages: state.canvasImages.filter((img) => img.id !== id),
    }));

    setTimeout(() => get().updateElementOrder(), 100);
  },

  selectedId: null,

  isEditingTextId: null,
  setIsEditingTextId: (id) => set({ isEditingTextId: id }),

  editingStyle: { top: 0, left: 0, width: 0, height: 0 },
  setEditingStyle: (style) => set({ editingStyle: style }),

  previewMode: false,
  setPreviewMode: (mode) => set({ previewMode: mode }),

  resetCanvas: () => {
    set({
      texts: [...DEFAULT_TEXT_ITEMS],
      backgroundColor: '#ffffff',
      bgImage: null,
      canvasImages: [],
      logoProps: { ...DEFAULT_LOGO_PROPS },
      selectedId: null,
      isEditingTextId: null,
      previewMode: false,
    });
  },

  undoChange: () => {
    const {
      history,
      future,
      setHistory,
      setFuture,
      setTexts,
      setLogoProps,
      setLogoImage,
      setBgImage,
      setBackgroundColor,
    } = get();

    if (history.length === 0) return;

    const state = get();
    const currentState = {
      texts: structuredClone(state.texts),
      logoProps: structuredClone(state.logoProps),
      logoImage: state.logoImage,
      bgImage: state.bgImage,
      backgroundColor: state.backgroundColor,
      canvasImages: structuredClone(state.canvasImages),
      timestamp: Date.now(),
    };

    const newHistory = [...history];
    const prevState = newHistory.pop();

    if (!prevState) return;

    setFuture([currentState, ...future]);

    setTexts(prevState.texts);
    setLogoProps(prevState.logoProps);
    setLogoImage(prevState.logoImage);
    setBgImage(prevState.bgImage);
    setBackgroundColor(prevState.backgroundColor);
    set({ canvasImages: prevState.canvasImages as CanvasImage[] });

    setHistory(newHistory);
  },

  redoChange: () => {
    const {
      history,
      future,
      setHistory,
      setFuture,
      setTexts,
      setLogoProps,
      setLogoImage,
      setBgImage,
      setBackgroundColor,
    } = get();

    if (future.length === 0) return;

    const state = get();
    const currentState = {
      texts: structuredClone(state.texts),
      logoProps: structuredClone(state.logoProps),
      logoImage: state.logoImage,
      bgImage: state.bgImage,
      backgroundColor: state.backgroundColor,
      canvasImages: structuredClone(state.canvasImages),
      timestamp: Date.now(),
    };

    const newFuture = [...future];
    const nextState = newFuture.shift();

    if (!nextState) return;

    setHistory([...history, currentState]);

    setTexts(nextState.texts);
    setLogoProps(nextState.logoProps);
    setLogoImage(nextState.logoImage);
    setBgImage(nextState.bgImage);
    setBackgroundColor(nextState.backgroundColor);
    set({ canvasImages: nextState.canvasImages as CanvasImage[] });

    setFuture(newFuture);
  },

  handleTextTransform: (id: string) => {
    const { textRefs, setTexts } = get();
    const textNode = textRefs.current?.get(id);

    if (!textNode) return;

    const konvaText = textNode as unknown as Konva.Text;
    const newWidth = Math.round(konvaText.width() * konvaText.scaleX());
    const newFontSize = Math.round(konvaText.fontSize() * konvaText.scaleY());

    const originalX = konvaText.x();
    const originalY = konvaText.y();

    setTexts((texts) => texts.map((t) => (t.id === id ? { ...t, width: newWidth, fontSize: newFontSize } : t)));

    konvaText.scaleX(1);
    konvaText.scaleY(1);

    setTimeout(() => {
      if (!textNode) return;

      const konvaNode = textNode as unknown as Konva.Node;
      konvaNode.x(originalX);
      konvaNode.y(originalY);
      konvaNode.width(newWidth);
      const layer = konvaNode.getLayer();
      if (layer) layer.batchDraw();
    }, 10);
  },

  handleLogoTransform: () => {
    const { logoRef, setLogoProps } = get();
    if (!logoRef.current) return;

    const konvaLogo = logoRef.current as unknown as Konva.Image;
    const newWidth = Math.round(konvaLogo.width() * konvaLogo.scaleX());
    const newHeight = Math.round(konvaLogo.height() * konvaLogo.scaleY());

    const originalX = konvaLogo.x();
    const originalY = konvaLogo.y();

    setLogoProps((prev) => ({
      ...prev,
      x: originalX,
      y: originalY,
      width: newWidth,
      height: newHeight,
    }));
  },

  handleTextDoubleClick: (id: string) => {
    const { setIsEditingTextId, textRefs, stageRef, setEditingStyle, setSelectedId } = get();

    const textNode = textRefs.current?.get(id);
    if (!textNode || !stageRef.current) return;

    const konvaText = textNode as unknown as Konva.Node;
    const rect = konvaText.getClientRect();
    const stageBox = stageRef.current.container().getBoundingClientRect();
    const borderAdjustment = 2;

    setEditingStyle({
      top: rect.y + stageBox.top - borderAdjustment,
      left: rect.x + stageBox.left - borderAdjustment,
      width: rect.width + borderAdjustment * 2,
      height: rect.height + borderAdjustment * 2,
    });

    setSelectedId(id);
    setIsEditingTextId(id);
  },

  handleLogoDoubleClick: () => {},

  deleteSelected: () => {
    const { selectedId, isEditingTextId, setIsEditingTextId, setSelectedId, setTexts, removeImage } = get();

    if (!selectedId || selectedId === 'canvas') return;

    if (isEditingTextId) setIsEditingTextId(null);

    get().saveStateToHistory();

    if (Array.isArray(selectedId)) {
      setSelectedId(null);

      selectedId.forEach((id) => {
        setTexts((prev) => prev.filter((t) => t.id !== id));
        removeImage(id);
      });

      return;
    }

    const id = selectedId;

    setTexts((prev) => prev.filter((t) => t.id !== id));
    removeImage(id);
  },

  setSelectedId: (id) => {
    set((state) => {
      if (state.selectedId !== id) {
        return {
          selectedId: id,
          isEditingTextId: null,
        };
      }
      return state;
    });
  },

  handleMultiSelect: (id, addToSelection) => {
    const { selectedId } = get();

    if (addToSelection) {
      if (selectedId === null || selectedId === 'canvas') {
        set({ selectedId: id });
      } else if (typeof selectedId === 'string') {
        if (selectedId !== id) {
          set({ selectedId: [selectedId, id] });
        }
      } else if (Array.isArray(selectedId)) {
        if (!selectedId.includes(id)) {
          set({ selectedId: [...selectedId, id] });
        }
      }
    } else {
      set({ selectedId: id });
    }
  },

  selectionRect: { startX: 0, startY: 0, width: 0, height: 0, visible: false },
  isMultiSelecting: false,
  selectedIds: [],

  moveSelectedElements: (dx: number, dy: number) => {
    set((state) => {
      if (!state.selectedId) return state;

      const ids = Array.isArray(state.selectedId) ? state.selectedId : [state.selectedId];
      if (ids.length === 0 || ids[0] === 'canvas') return state;

      if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
        if (state.saveStateToHistory) {
          state.saveStateToHistory();
        }
      }

      const newTexts = [...state.texts];
      const newCanvasImages = [...state.canvasImages];
      let updatedTexts = false;
      let updatedImages = false;

      ids.forEach((id) => {
        const textIndex = newTexts.findIndex((t) => t.id === id);
        if (textIndex !== -1) {
          newTexts[textIndex] = {
            ...newTexts[textIndex],
            x: newTexts[textIndex].x + dx,
            y: newTexts[textIndex].y + dy,
          };
          updatedTexts = true;
        }
      });

      ids.forEach((id) => {
        const imageIndex = newCanvasImages.findIndex((img) => img.id === id && !img.deleted);
        if (imageIndex !== -1) {
          newCanvasImages[imageIndex] = {
            ...newCanvasImages[imageIndex],
            x: newCanvasImages[imageIndex].x + dx,
            y: newCanvasImages[imageIndex].y + dy,
          };
          updatedImages = true;
        }
      });

      return {
        texts: updatedTexts ? newTexts : state.texts,
        canvasImages: updatedImages ? newCanvasImages : state.canvasImages,
      };
    });
  },

  updateMultipleTexts: (properties: Partial<TextItem>) => {
    set((state) => {
      if (!state.selectedId) return state;

      const ids = getSelectedIds(state.selectedId);
      if (ids.length === 0) return state;

      state.saveStateToHistory();

      const newTexts = state.texts.map((text) => {
        if (ids.includes(text.id)) {
          return { ...text, ...properties };
        }
        return text;
      });

      return { texts: newTexts };
    });
  },

  handleMultiTransform: (scaleX: number, scaleY: number, rotation: number) => {
    set((state) => {
      if (!state.selectedId) return state;

      const ids = getSelectedIds(state.selectedId);
      if (ids.length <= 1) return state;

      state.saveStateToHistory();

      const newTexts = [...state.texts];
      const newCanvasImages = [...state.canvasImages];
      let updatedTexts = false;
      let updatedImages = false;

      ids.forEach((id) => {
        const textIndex = newTexts.findIndex((t) => t.id === id);
        if (textIndex !== -1) {
          const text = newTexts[textIndex];
          newTexts[textIndex] = {
            ...text,
            fontSize: Math.round(text.fontSize * scaleY),
            width: Math.round(text.width * scaleX),
          };
          updatedTexts = true;
        }

        const imageIndex = newCanvasImages.findIndex((img) => img.id === id && !img.deleted);
        if (imageIndex !== -1) {
          const img = newCanvasImages[imageIndex];
          newCanvasImages[imageIndex] = {
            ...img,
            width: Math.round(img.width * scaleX),
            height: Math.round(img.height * scaleY),
            rotation: rotation,
          };
          updatedImages = true;
        }
      });

      return {
        texts: updatedTexts ? newTexts : state.texts,
        canvasImages: updatedImages ? newCanvasImages : state.canvasImages,
      };
    });
  },

  updateElementOrder: () => {
    const { stageRef, textRefs, logoRef, texts, canvasImages, logoImage } = get();

    if (!stageRef.current) {
      return;
    }

    const layers = stageRef.current.getLayers();
    if (!layers || layers.length === 0) {
      return;
    }

    const primaryLayerIndex = layers.length > 1 ? 1 : 0;
    const layer = layers[primaryLayerIndex];

    if (!layer) {
      return;
    }

    const elements: { node: Konva.Node; id: string; zIndex: number; type: string }[] = [];

    texts.forEach((text) => {
      const node = textRefs.current?.get(text.id);
      if (node) {
        elements.push({
          node: node as unknown as Konva.Node,
          id: text.id,
          zIndex: text.zIndex || 0,
          type: 'text',
        });
      }
    });

    canvasImages
      .filter((img) => !img.deleted)
      .forEach((img) => {
        try {
          let node = layer.findOne(`#${img.id}`);

          if (!node) {
            for (const searchLayer of layers) {
              node = searchLayer.findOne(`#${img.id}`);
              if (node) break;
            }
          }

          if (node) {
            elements.push({
              node,
              id: img.id,
              zIndex: img.zIndex || 0,
              type: 'image',
            });
          } else {
            setTimeout(() => {
              const state = get();
              if (state.stageRef.current) {
                state.stageRef.current.batchDraw();
              }
            }, 100);
          }
        } catch (err) {}
      });

    if (logoImage && logoRef.current) {
      elements.push({
        node: logoRef.current as unknown as Konva.Node,
        id: 'logo',
        zIndex: get().logoProps.zIndex || 0,
        type: 'logo',
      });
    }

    if (elements.length === 0) {
      return;
    }

    elements.sort((a, b) => a.zIndex - b.zIndex);

    const specialNodes: Konva.Node[] = [];
    layer.getChildren().forEach((child) => {
      if (child.getClassName() === 'Transformer') {
        specialNodes.push(child);
        child.moveToTop();
      }
    });

    elements.forEach((el, idx) => {
      try {
        if (idx === 0) {
          el.node.moveToBottom();
        } else {
          const prevNode = elements[idx - 1].node;
          el.node.moveUp();

          const siblings = el.node.getParent()?.getChildren() || [];
          const elIndex = siblings.indexOf(el.node);
          const prevIndex = siblings.indexOf(prevNode);

          if (elIndex < prevIndex) {
            el.node.moveToTop();
          }
        }
      } catch (err) {}
    });

    specialNodes.forEach((node) => {
      node.moveToTop();
    });

    layers.forEach((layer) => layer.batchDraw());
  },

  normalizeZIndices: () => {
    const { texts, canvasImages, logoImage, setTexts, logoProps, setLogoProps } = get();

    const elements: { type: 'text' | 'image' | 'logo'; id: string; zIndex: number }[] = [];

    texts.forEach((text) => {
      elements.push({
        type: 'text',
        id: text.id,
        zIndex: text.zIndex || 0,
      });
    });

    canvasImages
      .filter((img) => !img.deleted)
      .forEach((img) => {
        elements.push({
          type: 'image',
          id: img.id,
          zIndex: img.zIndex || 0,
        });
      });

    if (logoImage) {
      elements.push({
        type: 'logo',
        id: 'logo',
        zIndex: logoProps.zIndex || 0,
      });
    }

    elements.sort((a, b) => a.zIndex - b.zIndex);

    elements.forEach((element, index) => {
      const newZIndex = index + 1;

      if (element.type === 'text') {
        setTexts((prev) => prev.map((t) => (t.id === element.id ? { ...t, zIndex: newZIndex } : t)), false);
      } else if (element.type === 'image') {
        get().updateImage(element.id, { zIndex: newZIndex });
      } else if (element.type === 'logo') {
        setLogoProps((prev) => ({ ...prev, zIndex: newZIndex }), false);
      }
    });

    setTimeout(() => get().updateElementOrder(), 50);
  },

  moveElementUp: (id: string) => {
    const { texts, canvasImages, logoImage, logoProps, setTexts, setLogoProps, saveStateToHistory } = get();

    saveStateToHistory();

    const elements: { type: 'text' | 'image' | 'logo'; id: string; zIndex: number }[] = [];

    texts.forEach((text) => {
      elements.push({
        type: 'text',
        id: text.id,
        zIndex: text.zIndex || 0,
      });
    });

    canvasImages
      .filter((img) => !img.deleted)
      .forEach((img) => {
        elements.push({
          type: 'image',
          id: img.id,
          zIndex: img.zIndex || 0,
        });
      });

    if (logoImage) {
      elements.push({
        type: 'logo',
        id: 'logo',
        zIndex: logoProps.zIndex || 0,
      });
    }

    elements.sort((a, b) => a.zIndex - b.zIndex);

    const elementIndex = elements.findIndex((e) => e.id === id);
    if (elementIndex === -1 || elementIndex === elements.length - 1) return;

    const currentElement = elements[elementIndex];
    const upperElement = elements[elementIndex + 1];

    if (currentElement.type === 'text') {
      setTexts(
        (prev) => prev.map((t) => (t.id === currentElement.id ? { ...t, zIndex: upperElement.zIndex } : t)),
        false
      );
    } else if (currentElement.type === 'image') {
      get().updateImage(currentElement.id, { zIndex: upperElement.zIndex });
    } else if (currentElement.type === 'logo') {
      setLogoProps((prev) => ({ ...prev, zIndex: upperElement.zIndex }), false);
    }

    if (upperElement.type === 'text') {
      setTexts(
        (prev) => prev.map((t) => (t.id === upperElement.id ? { ...t, zIndex: currentElement.zIndex } : t)),
        false
      );
    } else if (upperElement.type === 'image') {
      get().updateImage(upperElement.id, { zIndex: currentElement.zIndex });
    } else if (upperElement.type === 'logo') {
      setLogoProps((prev) => ({ ...prev, zIndex: currentElement.zIndex }), false);
    }

    setTimeout(() => get().updateElementOrder(), 50);
  },

  moveElementDown: (id: string) => {
    const { texts, canvasImages, logoImage, logoProps, setTexts, setLogoProps, saveStateToHistory } = get();

    saveStateToHistory();

    const elements: { type: 'text' | 'image' | 'logo'; id: string; zIndex: number }[] = [];

    texts.forEach((text) => {
      elements.push({
        type: 'text',
        id: text.id,
        zIndex: text.zIndex || 0,
      });
    });

    canvasImages
      .filter((img) => !img.deleted)
      .forEach((img) => {
        elements.push({
          type: 'image',
          id: img.id,
          zIndex: img.zIndex || 0,
        });
      });

    if (logoImage) {
      elements.push({
        type: 'logo',
        id: 'logo',
        zIndex: logoProps.zIndex || 0,
      });
    }

    elements.sort((a, b) => a.zIndex - b.zIndex);

    const elementIndex = elements.findIndex((e) => e.id === id);
    if (elementIndex <= 0) return;

    const currentElement = elements[elementIndex];
    const lowerElement = elements[elementIndex - 1];

    if (currentElement.type === 'text') {
      setTexts(
        (prev) => prev.map((t) => (t.id === currentElement.id ? { ...t, zIndex: lowerElement.zIndex } : t)),
        false
      );
    } else if (currentElement.type === 'image') {
      get().updateImage(currentElement.id, { zIndex: lowerElement.zIndex });
    } else if (currentElement.type === 'logo') {
      setLogoProps((prev) => ({ ...prev, zIndex: lowerElement.zIndex }), false);
    }

    if (lowerElement.type === 'text') {
      setTexts(
        (prev) => prev.map((t) => (t.id === lowerElement.id ? { ...t, zIndex: currentElement.zIndex } : t)),
        false
      );
    } else if (lowerElement.type === 'image') {
      get().updateImage(lowerElement.id, { zIndex: currentElement.zIndex });
    } else if (lowerElement.type === 'logo') {
      setLogoProps((prev) => ({ ...prev, zIndex: currentElement.zIndex }), false);
    }

    setTimeout(() => get().updateElementOrder(), 50);
  },
}));

export const preloadLogo = () => new Image();
preloadLogo();

export const hasAnimationsSelector = (state: CanvasState): boolean => {
  return Boolean(
    state.texts.some((t: TextItem) => t.animation && t.animation !== 'none') ||
      (state.logoProps.animation && state.logoProps.animation !== 'none') ||
      state.canvasImages.some((img: CanvasImage) => img.animation && img.animation !== 'none')
  );
};
